import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { trpc } from '../lib/trpc';

/**
 * Bot type representing the currently selected project/bot.
 * Matches the shape returned by the bots.list API.
 */
export interface Bot {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  status: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * Project context value interface.
 */
export interface ProjectContextValue {
  /** The ID of the currently selected bot */
  currentBotId: string | null;
  /** The full details of the currently selected bot */
  currentBot: Bot | null;
  /** List of all available bots */
  bots: Bot[];
  /** Whether bots are currently loading */
  isLoading: boolean;
  /** Error if fetching bots failed */
  error: Error | null;
  /** Select a bot by ID */
  selectBot: (botId: string) => void;
  /** Clear the current selection */
  clearSelection: () => void;
  /** Refresh the bots list */
  refreshBots: () => void;
}

const STORAGE_KEY = 'admin_current_bot_id';

const ProjectContext = createContext<ProjectContextValue | undefined>(undefined);

/**
 * Provider component for global bot/project state.
 *
 * Automatically fetches the list of bots on initial load and restores
 * the last selected bot from localStorage, or selects the first available bot.
 */
export function ProjectProvider({ children }: { children: ReactNode }) {
  const [currentBotId, setCurrentBotId] = useState<string | null>(() => {
    // Try to restore from localStorage on initial load
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY);
    }
    return null;
  });

  // Fetch all active bots
  const botsQuery = trpc.bots.list.useQuery({
    status: 'active',
    limit: 100,
  });

  const bots = botsQuery.data?.bots ?? [];
  const isLoading = botsQuery.isLoading;
  const error = botsQuery.error as Error | null;

  // Find the current bot from the list
  const currentBot = currentBotId
    ? bots.find((bot) => bot.id === currentBotId) ?? null
    : null;

  // Persist selection to localStorage
  useEffect(() => {
    if (currentBotId) {
      localStorage.setItem(STORAGE_KEY, currentBotId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [currentBotId]);

  // Auto-select first bot if none selected and bots are available
  useEffect(() => {
    if (!isLoading && bots.length > 0) {
      // If no bot is selected, or the selected bot is no longer in the list
      if (!currentBotId || !bots.some((bot) => bot.id === currentBotId)) {
        // Try to restore from localStorage first
        const storedId = localStorage.getItem(STORAGE_KEY);
        if (storedId && bots.some((bot) => bot.id === storedId)) {
          setCurrentBotId(storedId);
        } else {
          // Otherwise select the first available bot
          setCurrentBotId(bots[0].id);
        }
      }
    }
  }, [isLoading, bots, currentBotId]);

  const selectBot = useCallback((botId: string) => {
    setCurrentBotId(botId);
  }, []);

  const clearSelection = useCallback(() => {
    setCurrentBotId(null);
  }, []);

  const refreshBots = useCallback(() => {
    botsQuery.refetch();
  }, [botsQuery]);

  const value: ProjectContextValue = {
    currentBotId,
    currentBot,
    bots,
    isLoading,
    error,
    selectBot,
    clearSelection,
    refreshBots,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

/**
 * Hook to access the project context.
 *
 * Must be used within a ProjectProvider.
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { currentBot, selectBot, bots } = useProject();
 *   // ...
 * }
 * ```
 */
export function useProject(): ProjectContextValue {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
}

export default ProjectContext;
